import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '@/lib/auth';
import { enrollPasskey } from '@/lib/auth';
import { isPasskeySupported } from '@/lib/passkey';

// Simple dropdown user menu. Click email → menu appears. Click outside → closes.

interface UserMenuProps {
  user: AuthUser;
  onLogout: () => void;
  onNavigateSettings: () => void;
}

export function UserMenu({ user, onLogout, onNavigateSettings }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const close = useCallback(() => setOpen(false), []);

  // Passkey enrollment (identity-login SSOT Stage 1). Adds a passkey to the
  // already-authenticated account; the user can then sign in with it (no password).
  const [passkeyStatus, setPasskeyStatus] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [passkeyMsg, setPasskeyMsg] = useState<string | null>(null);

  const onAddPasskey = useCallback(async () => {
    setPasskeyStatus('busy');
    setPasskeyMsg(null);
    try {
      await enrollPasskey('This device');
      setPasskeyStatus('done');
      setPasskeyMsg('Passkey added. You can now sign in with it.');
    } catch (err) {
      setPasskeyStatus('error');
      setPasskeyMsg(err instanceof Error ? err.message : 'Could not add a passkey.');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  return (
    <div className="sp-user-menu-wrapper" ref={ref}>
      <button
        className="sp-user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sp-user-menu-email mono">{user.email}</span>
        <span className="sp-user-menu-caret">{open ? '\u25B4' : '\u25BE'}</span>
      </button>

      {open && (
        <div className="sp-user-menu-dropdown">
          <div className="sp-user-menu-info">
            <div className="sp-user-menu-info-email">{user.email}</div>
            <span className={`sp-user-menu-role-badge ${user.isDirector ? 'sp-user-menu-role-badge--director' : ''}`}>
              {user.isDirector ? 'Director' : 'Tenant'}
            </span>
          </div>
          <div className="sp-user-menu-divider" />
          <button
            className="sp-user-menu-item"
            onClick={() => {
              close();
              navigate('/telegram');
            }}
          >
            Link Telegram
          </button>
          <button
            className="sp-user-menu-item"
            onClick={() => {
              close();
              onNavigateSettings();
            }}
          >
            Settings
          </button>
          {isPasskeySupported() && (
            <button
              className="sp-user-menu-item"
              onClick={onAddPasskey}
              disabled={passkeyStatus === 'busy'}
            >
              {passkeyStatus === 'busy' ? 'Adding passkey…' : 'Add a passkey'}
            </button>
          )}
          {passkeyMsg && (
            <div
              className="sp-user-menu-info"
              style={{
                fontSize: 11,
                color: passkeyStatus === 'error' ? 'var(--sp-error)' : 'var(--sp-text-dim)',
              }}
            >
              {passkeyMsg}
            </div>
          )}
          <button
            className="sp-user-menu-item sp-user-menu-item--danger"
            onClick={() => {
              close();
              onLogout();
            }}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
